import { Fragment, memo, useCallback } from 'react';
import { batch, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Button, ContextMenu, Item, SubMenu } from 'basic-components';
import { Add } from 'basic-components/icons';
import { DataLakeEnums } from 'dashboard-services/api/dataLake';
import { markdownEditorActions } from 'primary-components';

import { codeEditorActions, csvEditorActions, pythonEditorActions } from 'actions/modules/tools';
import { routing } from 'routing';

import ActionItem from 'primary/ActionItem';

import { Else, If, Then } from 'react-if';

import './CreateFileButton.scss';

const EMPTY_FUN = () => {}

const createFileButtonDefaultPropGetMenuButton = pathname => (
  <div key="MenuButton">
    <If condition={pathname.startsWith(`/${routing.dataLake.name}/${routing.dataLake.routing.groups.name}`)}>
      <Then>
        {() => (
          <ActionItem
              icon={(
                <Add
                    color="blue-bright"
                    height={24}
                    width={24}
                />
              )}
              onClick={EMPTY_FUN}
              text="Create file"
          />
        )}
      </Then>
      <Else>
        {() => (
          <div>
            <Button
                onClick={EMPTY_FUN}
                variant="primary"
            >
              Create file
            </Button>
          </div>
        )}
      </Else>
    </If>
  </div>
);

const CreateFileButton = ({ groupName = undefined, getMenuButton = createFileButtonDefaultPropGetMenuButton, onSubmitCallback = undefined }) => {
  const dispatch = useDispatch(),
        { pathname } = useLocation(),
        openCSVEditor = useCallback(() => {
          batch(() => {
            dispatch(csvEditorActions.changeValue(true, 'newFile'))
            dispatch(csvEditorActions.changeValue(true, 'editable'))
            dispatch(csvEditorActions.changeValue(true, 'open'))
            dispatch(csvEditorActions.changeValue(groupName, "groupName"))
            dispatch(csvEditorActions.changeValue(onSubmitCallback, "onSubmitCallback"))
          })
        }, [dispatch, groupName, onSubmitCallback]),
        openMarkdownEditor = useCallback(() => {
          batch(() => {
            dispatch(markdownEditorActions.changeValue(true, 'newFile'))
            dispatch(markdownEditorActions.changeValue(true, 'editable'))
            dispatch(markdownEditorActions.changeValue(true, 'open'))
            dispatch(markdownEditorActions.changeValue(false, 'isPreviewActive'))
            dispatch(markdownEditorActions.changeValue(onSubmitCallback, 'onSubmitCallback'))
            dispatch(markdownEditorActions.changeValue({ groupName, fileType: DataLakeEnums.FILE_TYPE.MARKDOWN }, 'fileInfo'))
          })
        }, [dispatch, groupName, onSubmitCallback]),
        openCodeEditor = useCallback(extension => {
          batch(() => {
            dispatch(codeEditorActions.changeValue(true, 'open'))
            dispatch(codeEditorActions.changeValue(true, 'editable'))
            dispatch(codeEditorActions.changeValue(true, 'newFile'))
            dispatch(codeEditorActions.changeValue(extension, 'extension'))
            dispatch(codeEditorActions.changeValue(groupName, 'groupName'))
            dispatch(codeEditorActions.changeValue(onSubmitCallback, 'onSubmitCallback'))
          })
        }, [dispatch, groupName, onSubmitCallback]),
        openTxtEditor = useCallback(() => openCodeEditor("txt"), [openCodeEditor]),
        openPyEditor = useCallback(() => {
          dispatch(pythonEditorActions.changeValue(true, 'isOpen'))
          dispatch(pythonEditorActions.changeValue(true, 'editable'))
          dispatch(pythonEditorActions.changeValue(true, 'newFile'))
          dispatch(pythonEditorActions.changeValue(groupName, 'groupName'))
          dispatch(pythonEditorActions.changeValue(onSubmitCallback, 'onSubmitCallback'))
        }, [dispatch, groupName, onSubmitCallback]),
        openJsonEditor = useCallback(() => openCodeEditor("json"), [openCodeEditor]),
        openShEditor = useCallback(() => openCodeEditor("sh"), [openCodeEditor]),
        openJsEditor = useCallback(() => openCodeEditor("js"), [openCodeEditor]),
        openSQLEditor = useCallback(() => openCodeEditor("sql"), [openCodeEditor])
  return (
    <ContextMenu
        align="center"
        className="ng-create-file-context-menu"
        gap={12}
        menuButton={getMenuButton(pathname)}
        size="large"
    >
    <Item onClick={openCSVEditor}>
      <div className="ng-create-file-context-menu__item__icon">
        <Add
            color="black-base"
            height={16}
            width={16}
        />
      </div>
        CSV
    </Item>
    <Item onClick={openTxtEditor}>
      <div className="ng-create-file-context-menu__item__icon">
        <Add
            color="black-base"
            height={16}
            width={16}
        />
      </div>
      Text
    </Item>
    <Item onClick={openJsonEditor}>
      <div className="ng-create-file-context-menu__item__icon">
        <Add
            color="black-base"
            height={16}
            width={16}
        />
      </div>
      JSON
    </Item>
    <SubMenu
        label={(
          <Fragment>
            <div className="ng-create-file-context-menu__item__icon">
              <Add
                  color="black-base"
                  height={16}
                  width={16}
              />
            </div>
            Script
          </Fragment>
        )}
    >
      <Item onClick={openPyEditor}>
        Python
      </Item>
      <Item onClick={openShEditor}>
        Bash
      </Item>
      <Item onClick={openJsEditor}>
        JavaScript
      </Item>
      <Item onClick={openSQLEditor}>
        SQL
      </Item>
    </SubMenu>
    <Item onClick={openMarkdownEditor}>
      <div className="ng-create-file-context-menu__item__icon">
        <Add
            color="black-base"
            height={16}
            width={16}
        />
      </div>
        Markdown
    </Item>
    </ContextMenu>
  )
}

CreateFileButton.propTypes = {
  getMenuButton: PropTypes.func,
  groupName: PropTypes.string,
  onSubmitCallback: PropTypes.func,
}

export default memo(CreateFileButton)